import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Button } from "../ui/button";
import Flag from "../Flag";
import {
  ShoppingCart,
  Trash2,
  CheckCircle2,
  ImageOff,
  ArrowLeft,
  Check,
  Building2,
  Receipt,
  Landmark,
  MapPin,
  Banknote,
} from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _resolve = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const ERP_OPTIONS = [
  { key: "coupa", name: "Coupa", domain: "coupa.com", tagline: "Procure-to-Pay · BSM", protocol: "PunchOut cXML 1.2.043" },
  { key: "ariba", name: "SAP Ariba", domain: "ariba.com", tagline: "Procurement & Buying", protocol: "PunchOut cXML 1.2.045" },
  { key: "aerchain", name: "Aerchain", domain: "aerchain.io", tagline: "Sourcing & Procurement", protocol: "PunchOut OCI 4.0" },
  { key: "jaggaer", name: "Jaggaer", domain: "jaggaer.com", tagline: "Direct & Indirect", protocol: "PunchOut cXML 1.2.043" },
  { key: "ivalua", name: "ivalua", domain: "ivalua.com", tagline: "Unified Source-to-Pay", protocol: "PunchOut cXML 1.2.045" },
  { key: "sap_s4", name: "SAP S/4HANA", domain: "sap.com", tagline: "Enterprise Procurement", protocol: "OCI 5.0 over HTTPS" },
  { key: "oracle", name: "Oracle Fusion", domain: "oracle.com", tagline: "Oracle iProcurement", protocol: "PunchOut cXML 1.2.043" },
  { key: "other", name: "Another system", domain: null, tagline: "Workday · ServiceNow · Custom", protocol: "Discovery call to scope" },
];

function LineThumb({ src, alt, pn }) {
  if (src && (/^https?:\/\//i.test(src) || src.startsWith("/api/"))) {
    return (
      <img
        src={_resolve(src)}
        alt={alt}
        loading="lazy"
        data-testid={`cart-line-thumb-${pn}`}
        className="h-16 w-16 shrink-0 rounded-md object-cover bg-slate-50 border border-slate-200"
      />
    );
  }
  return (
    <div className="h-16 w-16 shrink-0 rounded-md flex items-center justify-center bg-slate-50 border border-slate-200 text-slate-400">
      <ImageOff size={20} strokeWidth={1.5} />
    </div>
  );
}

function ErpLogo({ erp, size = 36 }) {
  const tint = {
    coupa: "#FE5000", ariba: "#0F6BFF", aerchain: "#0EA5E9", jaggaer: "#16A34A",
    ivalua: "#7C3AED", sap_s4: "#003366", oracle: "#C74634", other: "#475569",
  };
  const [failed, setFailed] = React.useState(false);
  const monogram = (
    <div
      className="rounded-md flex items-center justify-center font-display font-bold text-white shrink-0 shadow-sm"
      style={{ height: size, width: size, background: tint[erp.key] || "#475569", fontSize: size * 0.42 }}
    >
      {erp.key === "sap_s4" ? "S/4" : erp.name.slice(0, 1).toUpperCase()}
    </div>
  );
  if (!erp.domain || failed) return monogram;
  const src = `https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${erp.domain}&size=128`;
  return (
    <div
      className="rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0 shadow-sm"
      style={{ height: size, width: size }}
    >
      <img
        src={src}
        alt={`${erp.name} logo`}
        onError={() => setFailed(true)}
        className="object-contain"
        style={{ height: size * 0.78, width: size * 0.78 }}
        data-testid={`erp-logo-${erp.key}`}
      />
    </div>
  );
}

function EntityRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-b-0">
      <div className="h-8 w-8 rounded-md bg-[#0072CE]/8 border border-[#0072CE]/15 text-[#0072CE] flex items-center justify-center shrink-0">
        <Icon size={15} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500">{label}</div>
        <div className="font-display text-sm text-slate-900 leading-snug mt-0.5 break-words" data-testid={testid}>
          {value}
        </div>
      </div>
    </div>
  );
}

export default function CartDrawer({ open, onOpenChange, cart, onChangeQty, onRemove, onTransfer, transferring, persona }) {
  const { t, fmt, fmtMoney, currency: personaCurrency } = useLocale();
  // step: "cart" → "pick-entity" → "pick-erp" → "confirmation"
  const [step, setStep] = useState("cart");
  const [selectedErp, setSelectedErp] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  // ─── Origin PunchOut session (Coupa / Ariba / Aerchain) ──────────────
  // If the buyer arrived via an origin POSR (?punchout_session=…) we
  // surface a banner inside the cart and offer a "Transfer Unified Cart
  // back to Origin" button that posts a cXML PunchOutOrderMessage.
  const [originSession, setOriginSession] = useState(null);
  const [originTransferring, setOriginTransferring] = useState(false);
  const [originResult, setOriginResult] = useState(null);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("punchout_session");
    if (!sid) {
      setOriginSession(null);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/punchout/session/${sid}`);
        setOriginSession(data);
      } catch {
        setOriginSession(null);
      }
    })();
  }, [open]);

  const transferToOrigin = async () => {
    if (!originSession) return;
    setOriginTransferring(true);
    try {
      // v3.9.10 — send the SESSION identity (the buyer who punched in),
      // not the persona. The backend keys the cart by session identity,
      // so the assembled POOM always matches the cart on screen.
      const { data } = await axios.post(`${BACKEND_URL}/api/punchout/transfer-to-origin`, {
        session_id: originSession.session_id,
        network_id: originSession.network_id || persona?.network_id,
        company_name: originSession.company_name || persona?.company_name,
        country_code: originSession.country_code || persona?.country_code,
      });

      // cXML BrowserFormPost contract: the BUYER'S BROWSER must POST the
      // PunchOutOrderMessage to the origin's BrowserFormPost/URL as
      // application/x-www-form-urlencoded with a single `cxml-urlencoded`
      // field. The origin responds 303 + Location, which navigates the
      // buyer back to Aerchain/Coupa/Ariba. Skipped for the bundled mock
      // origin (same host) — the simulator page renders the POOM itself.
      const postUrl = data.browser_form_post_url || "";
      let external = false;
      try {
        external = /^https?:\/\//i.test(postUrl) && new URL(postUrl).host !== window.location.host;
      } catch { external = false; }

      if (external) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = postUrl;
        form.enctype = "application/x-www-form-urlencoded";
        form.style.display = "none";
        const field = document.createElement("input");
        field.type = "hidden";
        field.name = data.browser_post_field || "cxml-urlencoded";
        field.value = data.cxml;
        form.appendChild(field);
        document.body.appendChild(form);
        form.submit();
        return; // browser navigates away to the origin
      }
      setOriginResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Transfer to origin failed");
      console.error("[CartDrawer] transfer-to-origin failed:", err);
    } finally {
      setOriginTransferring(false);
    }
  };

  const lines = cart?.lines || [];
  const max = cart?.max || 6;
  const total = lines.reduce((s, l) => s + l.unit_price * l.quantity, 0);
  const entity = persona?.selling_entity;

  // ─── Live tax quote (Avalara-ready) ──────────────────────────────────
  // Whenever the cart contents or the buyer's country changes, fetch a
  // server-side tax quote so the subtotal block shows NET + tax + GROSS.
  const [taxQuote, setTaxQuote] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!open || lines.length === 0 || !persona?.country_code) {
      setTaxQuote(null);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/infoshop/tax/quote`, {
          country_code: persona.country_code,
          network_id: persona.network_id,
          company_name: persona.company_name,
          lines: lines.map((l) => ({
            infoshop_pn: l.infoshop_pn,
            unspsc: l.unspsc || "",
            unit_price: l.unit_price,
            currency: l.currency,
            quantity: l.quantity,
            // Sub-supplier PunchOut lines are supplier-priced — backend
            // tax engine skips UniBuy™ VAT for these so we don't
            // double-tax Grainger / Fastenal / ZKH contract pricing.
            source_type: l.source_type || "hosted",
          })),
        });
        if (!cancelled) setTaxQuote(data);
      } catch {
        if (!cancelled) setTaxQuote(null);
      }
    })();
    return () => { cancelled = true; };
    // Re-quote whenever the cart shape changes
     
  }, [open, persona?.country_code, JSON.stringify(lines.map((l) => [l.infoshop_pn, l.quantity]))]);

  const goToEntity = () => {
    if (lines.length === 0) return;
    setSelectedErp(null);
    setStep("pick-entity");
  };

  const handleConfirmErp = async () => {
    if (!selectedErp) return;
    const res = await onTransfer({ destination: selectedErp.key });
    if (res) {
      setConfirmation({ ...res, erp: selectedErp, entity });
      setStep("confirmation");
    }
  };

  const close = (o) => {
    if (!o) {
      setConfirmation(null);
      setSelectedErp(null);
      setStep("cart");
    }
    onOpenChange(o);
  };

  return (
    <Sheet open={open} onOpenChange={close}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        {step === "cart" && (
          <>
            <SheetHeader>
              <SheetTitle className="font-display tracking-tight text-2xl inline-flex items-center gap-2">
                <ShoppingCart size={20} /> {t("cart.title")}
              </SheetTitle>
              <SheetDescription>
                <span data-testid="cart-id">{cart?.cart_id || "—"}</span> · {lines.length}/{max} {t("cart.line_count")}.
                {" "}
                {t("cart.max_items")}
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              {/* Origin PunchOut session banner — shown only when the buyer
                  arrived inside UniBuy™ via a POSR from Coupa/Ariba/Aerchain.
                  Danone punches in from Coupa (via Aerchain relay); at the
                  cart-transfer moment we present it as a plain Coupa cXML
                  Cart Transfer — Aerchain is infrastructure Danone buyers
                  should never see. */}
              {originSession && !originResult && (() => {
                const idStr = [
                  originSession.from_identity,
                  originSession.origin_label,
                  originSession.company_name,
                  originSession.network_id,
                ].filter(Boolean).join(" ").toUpperCase();
                const isDanone = idStr.includes("DANONE");
                const originBrand = isDanone ? "Coupa" : (originSession.origin_label || "Origin");
                const originIdentity = isDanone
                  ? "Danone · Coupa"
                  : (originSession.from_identity || "your eProcurement system");
                const transferLabel = isDanone
                  ? (originTransferring ? "Posting Coupa cXML…" : "Coupa cXML Cart Transfer")
                  : (originTransferring ? "Posting cXML…" : "Transfer Unified Cart to Origin");
                return (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50 p-3"
                    data-testid="origin-punchout-banner"
                    data-origin-brand={isDanone ? "coupa" : (originSession.origin_label || "origin").toLowerCase()}
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-800">
                        PunchOut session active from {originBrand}
                      </div>
                    </div>
                    <div className="text-xs text-amber-900 mt-1 leading-snug">
                      Transfer this unified cart (UniBuy™ + sub-supplier items) back to{" "}
                      <span className="font-semibold">{originIdentity}</span>
                      {" "}as a single cXML PunchOutOrderMessage.
                    </div>
                    <Button
                      onClick={transferToOrigin}
                      disabled={lines.length === 0 || originTransferring}
                      data-testid="origin-transfer-btn"
                      className="mt-2 w-full bg-amber-500 hover:bg-amber-600 text-amber-950 font-semibold rounded-md py-2.5 text-sm"
                    >
                      {transferLabel}
                    </Button>
                  </div>
                );
              })()}
              {originResult && (
                <div
                  className="rounded-lg border border-emerald-300 bg-emerald-50 p-3"
                  data-testid="origin-transferred-confirm"
                >
                  <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-emerald-800">
                    <CheckCircle2 size={11} /> PunchOutOrderMessage posted
                  </div>
                  <div className="text-xs text-emerald-900 mt-1 leading-snug">
                    {originResult.item_count} line(s) · {originResult.currency} {Number(originResult.total).toFixed(2)} returned to the origin ERP.
                    UniBuy™ has captured the sub-supplier routing internally for downstream PO issuance.
                  </div>
                </div>
              )}

              {lines.length === 0 && (
                <div className="text-sm text-slate-500 py-12 text-center">
                  {t("cart.empty")}
                </div>
              )}
              {lines.map((l) => (
                <div key={l.infoshop_pn} data-testid={`cart-line-${l.infoshop_pn}`} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <LineThumb src={l.image_url} alt={l.description} pn={l.infoshop_pn} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{l.category}</div>
                        {l.source_type === "sub_supplier_punchout" ? (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-[#0072CE] bg-[#0072CE]/10 border border-[#0072CE]/20 rounded px-1.5 py-0.5"
                            data-testid={`cart-line-source-${l.infoshop_pn}`}
                          >
                            via {l.sub_supplier_name || "Sub-supplier"}
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"
                            data-testid={`cart-line-source-${l.infoshop_pn}`}
                          >
                            UniBuy™ catalog
                          </span>
                        )}
                      </div>
                      <div className="font-display text-sm font-semibold text-slate-900 leading-tight mt-0.5">{l.description}</div>
                      <div className="text-xs text-slate-500 mt-1 font-mono">{l.infoshop_pn} · {l.brand}</div>
                    </div>
                    <button onClick={() => onRemove(l.infoshop_pn)} data-testid={`cart-remove-${l.infoshop_pn}`} className="text-slate-400 hover:text-rose-600 shrink-0" aria-label="Remove">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center border border-slate-300 rounded-md overflow-hidden">
                      <button onClick={() => onChangeQty(l.infoshop_pn, Math.max(1, l.quantity - 1))} data-testid={`cart-dec-${l.infoshop_pn}`} className="px-2.5 py-1 text-slate-600 hover:bg-slate-50">−</button>
                      <span className="w-10 text-center text-sm font-medium" data-testid={`cart-qty-${l.infoshop_pn}`}>{l.quantity}</span>
                      <button onClick={() => onChangeQty(l.infoshop_pn, l.quantity + 1)} data-testid={`cart-inc-${l.infoshop_pn}`} className="px-2.5 py-1 text-slate-600 hover:bg-slate-50">+</button>
                    </div>
                    <div className="text-right">
                      {l.source_type === "sub_supplier_punchout" && l.vendor_price && l.vendor_price > l.unit_price ? (
                        <>
                          <div className="text-[10px] text-slate-400 line-through" data-testid={`cart-line-vendor-price-${l.infoshop_pn}`}>
                            {fmtMoney(l.vendor_price, { source_type: l.source_type, currency: l.currency })}
                          </div>
                          <div className="text-xs text-slate-500">{fmtMoney(l.unit_price, { source_type: l.source_type, currency: l.currency })} × {l.quantity}</div>
                          <div className="font-display text-sm font-bold text-slate-900">{fmtMoney(l.unit_price * l.quantity, { source_type: l.source_type, currency: l.currency })}</div>
                          <span
                            data-testid={`cart-line-savings-chip-${l.infoshop_pn}`}
                            title={`Category-level discount vs. vendor list price · ${l.pricing_label || ""}`}
                            className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-wider font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                          >
                            {l.pricing_label || `You save ${(l.effective_customer_discount_pct || 0).toFixed(2)}%`}
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="text-xs text-slate-500">{fmtMoney(l.unit_price, { source_type: l.source_type, currency: l.currency })} × {l.quantity}</div>
                          <div className="font-display text-sm font-bold text-slate-900">{fmtMoney(l.unit_price * l.quantity, { source_type: l.source_type, currency: l.currency })}</div>
                          {l.source_type === "sub_supplier_punchout" && !l.vendor_price && (
                            <span
                              data-testid={`cart-line-best-price-${l.infoshop_pn}`}
                              className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-wider font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5"
                            >
                              Best price on UniBuy™
                            </span>
                          )}
                          {l.source_type === "sub_supplier_punchout" && l.vendor_price && l.vendor_price === l.unit_price && (
                            <span
                              data-testid={`cart-line-best-price-${l.infoshop_pn}`}
                              className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-wider font-bold text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5"
                            >
                              Best price on UniBuy™
                            </span>
                          )}
                        </>
                      )}
                      {l.source_type === "sub_supplier_punchout" && l.currency && l.currency !== personaCurrency && (
                        <div
                          className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5"
                          data-testid={`cart-line-native-currency-${l.infoshop_pn}`}
                          title={`Priced in ${l.currency} by ${l.sub_supplier_name || "sub-supplier"} — no FX applied by UniBuy™`}
                        >
                          {l.currency} · partner-native
                        </div>
                      )}
                      {l.source_type === "sub_supplier_punchout" && (
                        l.supplier_tax_included && l.supplier_tax_amount > 0 ? (
                          <span
                            data-testid={`cart-line-supplier-tax-incl-${l.infoshop_pn}`}
                            title={
                              `${l.supplier_tax_description || "Tax"} ${l.supplier_tax_pct ? `· ${l.supplier_tax_pct}%` : ""} reported by ${l.sub_supplier_name || "sub-supplier"}`
                            }
                            className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-wider font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                          >
                            Incl. tax · {fmtMoney(l.supplier_tax_amount, { source_type: l.source_type, currency: l.currency })}
                            {l.supplier_tax_pct ? ` (${l.supplier_tax_pct}%)` : ""}
                          </span>
                        ) : (
                          <span
                            data-testid={`cart-line-supplier-tax-not-incl-${l.infoshop_pn}`}
                            title={`${l.sub_supplier_name || "Sub-supplier"} did not return tax on this line — UniBuy™ will compute landed tax at checkout`}
                            className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-wider font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                          >
                            Tax not included
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-200 pt-4 space-y-3">
              {(() => {
                // v3.9.7 — When the cart mixes multiple currencies, a
                // single "Total" number is meaningless (you can't
                // meaningfully sum USD + EUR + CNY). We suppress the
                // legacy top-line total in favour of the per-bucket
                // native-currency amounts rendered by the split-preview
                // below.
                const cartCurrencies = Array.from(new Set(lines.map((l) => l.currency).filter(Boolean)));
                const isMultiCurrency = cartCurrencies.length > 1;
                return isMultiCurrency ? (
                  <div
                    className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-[12px] leading-snug text-amber-900"
                    data-testid="cart-multi-currency-notice"
                  >
                    <div className="font-semibold inline-flex items-center gap-1.5">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                      Mixed-currency cart · totals shown per bucket below
                    </div>
                    <div className="text-[11px] mt-0.5 text-amber-800">
                      This cart holds {cartCurrencies.join(" + ")} lines. UniBuy™ preserves
                      each partner&apos;s native currency verbatim — no FX conversion — and
                      transfers as {cartCurrencies.length} separate orders (see below).
                    </div>
                  </div>
                ) : taxQuote ? (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-1.5" data-testid="cart-tax-block">
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-slate-500">{t("cart.subtotal_net")}</span>
                    <span className="font-mono text-slate-700">{fmt(taxQuote.subtotal_net)}</span>
                  </div>
                  {taxQuote.exemption_applied ? (
                    <div
                      className="rounded-sm bg-emerald-50 border border-emerald-200 px-2.5 py-2 text-emerald-800 text-[12px] leading-snug"
                      data-testid="cart-tax-exempt"
                    >
                      <div className="font-semibold inline-flex items-center gap-1.5">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {t("cart.tax_exempt_title")}
                      </div>
                      <div className="text-[11px] mt-0.5 font-mono text-emerald-700">
                        {taxQuote.exemption_certificate_number}
                        {taxQuote.exemption_reason ? ` · ${taxQuote.exemption_reason}` : ""}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-slate-500 inline-flex items-center gap-1.5">
                        {taxQuote.tax_label}
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 bg-white border border-slate-200 rounded px-1.5 py-0.5">
                          {taxQuote.display_mode === "inclusive" ? t("cart.tax_incl_label") : t("cart.tax_excl_label")}
                        </span>
                      </span>
                      <span className="font-mono text-slate-700" data-testid="cart-tax-amount">{fmt(taxQuote.total_tax)}</span>
                    </div>
                  )}
                  <div className="flex items-baseline justify-between pt-1.5 border-t border-slate-200">
                    <span className="text-sm font-semibold text-slate-900">{t("cart.total_gross")}</span>
                    <span data-testid="cart-total" className="font-display text-2xl font-bold text-slate-900">
                      {fmt(taxQuote.grand_total_gross)}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono leading-tight pt-1">
                    {taxQuote.jurisdiction} · {taxQuote.engine}
                  </div>
                </div>
              ) : (
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-slate-500">{t("cart.subtotal")}</span>
                  <span data-testid="cart-total" className="font-display text-2xl font-bold text-slate-900">{fmt(total)}</span>
                </div>
              );
              })()}
              <Button
                onClick={goToEntity}
                disabled={lines.length === 0 || transferring}
                data-testid="cart-transfer-btn"
                className="w-full bg-[#0072CE] hover:bg-[#005BA6] text-white rounded-md py-5 font-semibold"
              >
                {t("cart.transfer_btn")}
              </Button>
              {/* Split preview (v3.8.6 #bucket-split · v3.9.6 polish) —
                  When the cart spans more than one (currency × tax-class)
                  bucket, we render a collapsible card per bucket showing
                  its subtotal, supplier tax, line count, and destination
                  ERP so the buyer sees EXACTLY what N orders will look
                  like before hitting Transfer. Calculated client-side
                  using the same bucketing rule as the backend. */}
              {(() => {
                const tcOf = (l) => {
                  if (l.source_type !== "sub_supplier_punchout") return "hosted";
                  return l.supplier_tax_included ? "sub_supplier_tax_incl" : "sub_supplier_tax_excl";
                };
                const buckets = {};
                for (const l of lines) {
                  const key = `${l.currency}|${tcOf(l)}`;
                  if (!buckets[key]) {
                    buckets[key] = {
                      key,
                      currency: l.currency,
                      tax_class: tcOf(l),
                      lines: [],
                      subtotal: 0,
                      supplier_tax: 0,
                      supplier_names: new Set(),
                    };
                  }
                  const b = buckets[key];
                  b.lines.push(l);
                  b.subtotal += l.unit_price * l.quantity;
                  b.supplier_tax += l.supplier_tax_amount || 0;
                  if (l.sub_supplier_name) b.supplier_names.add(l.sub_supplier_name);
                }
                const bucketList = Object.values(buckets).sort((a, b) => b.subtotal - a.subtotal);
                if (bucketList.length < 2) return null;

                const TAX_CLASS_LABEL = {
                  hosted: "Hosted catalog · UniBuy™-taxed",
                  sub_supplier_tax_incl: "Sub-supplier · tax-inclusive",
                  sub_supplier_tax_excl: "Sub-supplier · tax not included",
                };
                const TAX_CLASS_ACCENT = {
                  hosted: { bg: "bg-sky-50", border: "border-sky-200", dot: "bg-sky-500", text: "text-sky-800" },
                  sub_supplier_tax_incl: { bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500", text: "text-emerald-800" },
                  sub_supplier_tax_excl: { bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500", text: "text-amber-800" },
                };
                const erp = "SAP Ariba"; // default ERP surfaced pre-transfer; overwritten by the real erp_system in Step 2 (entity pick)
                return (
                  <div
                    data-testid="cart-split-preview"
                    className="rounded-md border border-amber-200 bg-amber-50/60 overflow-hidden"
                  >
                    <div className="px-3 py-2 text-[11px] text-amber-900 leading-snug border-b border-amber-200/60">
                      <strong>Cart will split into {bucketList.length} separate orders</strong> on checkout —
                      UniBuy™ never combines lines with different tax conventions (inclusive vs not-included)
                      or currencies into one ERP PO.
                    </div>
                    <div className="divide-y divide-slate-100 bg-white">
                      {bucketList.map((b, idx) => {
                        const a = TAX_CLASS_ACCENT[b.tax_class];
                        const suppliers = Array.from(b.supplier_names);
                        return (
                          <details
                            key={b.key}
                            open={idx === 0}
                            data-testid={`cart-split-bucket-${b.tax_class}-${b.currency}`}
                            className="group"
                          >
                            <summary className="list-none cursor-pointer px-3 py-2 flex items-center gap-2.5 hover:bg-slate-50 select-none">
                              <span className={`h-2 w-2 rounded-full ${a.dot} flex-none`} />
                              <span className="flex-none inline-flex items-center gap-1.5">
                                <span className="text-[10px] font-mono font-bold text-slate-500">#{idx + 1}</span>
                                <span className={`inline-block text-[9px] uppercase tracking-wider font-bold ${a.bg} ${a.text} border ${a.border} rounded px-1.5 py-0.5`}>
                                  {TAX_CLASS_LABEL[b.tax_class]}
                                </span>
                              </span>
                              <span className="ml-auto text-right leading-tight">
                                <div className="font-display text-sm font-bold text-slate-900 tabular-nums">
                                  {new Intl.NumberFormat("en-US", { style: "currency", currency: b.currency }).format(b.subtotal)}
                                </div>
                                <div className="text-[10px] text-slate-500 font-mono">
                                  {b.lines.length} line{b.lines.length !== 1 ? "s" : ""}
                                </div>
                              </span>
                              <svg className="h-3 w-3 text-slate-400 flex-none transition-transform group-open:rotate-180" viewBox="0 0 12 12" fill="none">
                                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </summary>
                            <div className="px-3 pb-3 pt-1 space-y-1.5 bg-slate-50/50">
                              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                                <span className="text-slate-500">Subtotal</span>
                                <span className="text-right font-mono text-slate-800">
                                  {new Intl.NumberFormat("en-US", { style: "currency", currency: b.currency }).format(b.subtotal)}
                                </span>
                                {b.supplier_tax > 0 && (
                                  <>
                                    <span className="text-slate-500">Supplier tax (line-level)</span>
                                    <span className="text-right font-mono text-emerald-700">
                                      +{new Intl.NumberFormat("en-US", { style: "currency", currency: b.currency }).format(b.supplier_tax)}
                                    </span>
                                  </>
                                )}
                                <span className="text-slate-500">Destination ERP</span>
                                <span className="text-right font-semibold text-slate-800">{erp}</span>
                                {suppliers.length > 0 && (
                                  <>
                                    <span className="text-slate-500">Supplier{suppliers.length > 1 ? "s" : ""}</span>
                                    <span className="text-right text-slate-800 truncate">{suppliers.join(", ")}</span>
                                  </>
                                )}
                              </div>
                              <div className="pt-1.5 border-t border-slate-200/60 space-y-0.5">
                                {b.lines.slice(0, 5).map((ln) => (
                                  <div key={ln.infoshop_pn} className="flex items-center gap-2 text-[10px]">
                                    <span className="font-mono text-slate-500 flex-none">{ln.infoshop_pn}</span>
                                    <span className="text-slate-600 truncate flex-1">{ln.description}</span>
                                    <span className="font-mono text-slate-700 flex-none tabular-nums">×{ln.quantity}</span>
                                  </div>
                                ))}
                                {b.lines.length > 5 && (
                                  <div className="text-[10px] text-slate-400 italic pt-0.5">
                                    + {b.lines.length - 5} more line{b.lines.length - 5 !== 1 ? "s" : ""}…
                                  </div>
                                )}
                              </div>
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {t("cart.transfer_help")}
              </p>
            </div>
          </>
        )}

        {step === "pick-entity" && entity && (
          <>
            <SheetHeader>
              <button
                onClick={() => setStep("cart")}
                data-testid="entity-back-to-cart"
                className="text-xs text-slate-500 hover:text-[#0072CE] inline-flex items-center gap-1 mb-3 self-start"
              >
                <ArrowLeft size={14} /> {t("entity.back")}
              </button>
              <div className="inline-flex items-center gap-2 mb-1">
                <Flag code={entity.country_code} height={16} />
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0072CE]">
                  {persona.country_name} · {entity.currency}
                </span>
              </div>
              <SheetTitle className="font-display tracking-tight text-2xl">
                {t("entity.title")}
              </SheetTitle>
              <SheetDescription className="text-sm text-slate-600 leading-relaxed">
                {t("entity.subtitle")}
              </SheetDescription>
            </SheetHeader>

            <div
              className="mt-4 rounded-xl border border-slate-200 bg-white p-4 overflow-y-auto"
              data-testid="selling-entity-card"
            >
              <EntityRow icon={Building2} label={t("entity.legal_name")} value={entity.legal_name} testid="entity-legal-name" />
              <EntityRow icon={Receipt}   label={t("entity.tax_id")}     value={entity.tax_id}     testid="entity-tax-id" />
              <EntityRow icon={MapPin}    label={t("entity.address")}    value={entity.address}    testid="entity-address" />
              <EntityRow icon={Landmark}  label={t("entity.bank")}       value={entity.bank}       testid="entity-bank" />
              <EntityRow icon={Banknote}  label={t("entity.currency")}   value={entity.currency}   testid="entity-currency" />
            </div>

            <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-3 space-y-1" data-testid="entity-cart-summary">
              {taxQuote ? (
                <>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="text-slate-500">{t("cart.subtotal_net")}</span>
                    <span className="font-mono text-slate-700">{fmt(taxQuote.subtotal_net)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="text-slate-500">{taxQuote.tax_label} {taxQuote.lines[0]?.tax_rate_pct ?? ""}%</span>
                    <span className="font-mono text-slate-700">{fmt(taxQuote.total_tax)}</span>
                  </div>
                  <div className="flex items-baseline justify-between pt-1 border-t border-slate-200">
                    <span className="text-xs font-semibold text-slate-900">{t("cart.total_gross")}</span>
                    <span className="font-display text-lg font-bold text-slate-900" data-testid="entity-cart-total">
                      {fmt(taxQuote.grand_total_gross)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-slate-500">{t("cart.subtotal")}</span>
                  <span className="font-display text-lg font-bold text-slate-900" data-testid="entity-cart-total">{fmt(total)}</span>
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 pt-4 mt-4 space-y-3">
              <Button
                onClick={() => setStep("pick-erp")}
                data-testid="entity-confirm-btn"
                className="w-full bg-[#0072CE] hover:bg-[#005BA6] text-white rounded-md py-5 font-semibold"
              >
                {t("entity.continue")}
              </Button>
            </div>
          </>
        )}

        {step === "pick-erp" && (
          <>
            <SheetHeader>
              <button
                onClick={() => setStep("pick-entity")}
                data-testid="erp-back-to-entity"
                className="text-xs text-slate-500 hover:text-[#0072CE] inline-flex items-center gap-1 mb-3 self-start"
              >
                <ArrowLeft size={14} /> {t("entity.title")}
              </button>
              <SheetTitle className="font-display tracking-tight text-2xl">
                {t("erp.title")}
              </SheetTitle>
              <SheetDescription>
                {t("erp.help")}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-2 overflow-y-auto pr-1" data-testid="erp-picker-list">
              {ERP_OPTIONS.map((erp) => {
                const active = selectedErp?.key === erp.key;
                return (
                  <button
                    key={erp.key}
                    onClick={() => setSelectedErp(erp)}
                    data-testid={`erp-option-${erp.key}`}
                    className={`w-full text-left flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                      active
                        ? "border-[#0072CE] bg-[#0072CE]/5 ring-2 ring-[#0072CE]/20"
                        : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <ErpLogo erp={erp} />
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold text-slate-900 text-sm">{erp.name}</div>
                      <div className="text-[11px] text-slate-500">{erp.tagline} · {erp.protocol}</div>
                    </div>
                    {active && (
                      <div className="h-5 w-5 rounded-full bg-[#0072CE] text-white flex items-center justify-center shrink-0">
                        <Check size={12} strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="border-t border-slate-200 pt-4 mt-4 space-y-3">
              <Button
                onClick={handleConfirmErp}
                disabled={!selectedErp || transferring}
                data-testid="erp-confirm-btn"
                className="w-full bg-[#0072CE] hover:bg-[#005BA6] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md py-5 font-semibold"
              >
                {transferring
                  ? t("erp.transferring")
                  : selectedErp
                    ? `${t("erp.transfer_to")} ${selectedErp.name}`
                    : t("erp.select_to_continue")}
              </Button>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {entity ? `${entity.legal_name} · ${entity.tax_id}` : ""}
              </p>
            </div>
          </>
        )}

        {step === "confirmation" && confirmation && (
          <>
            <SheetHeader>
              <div className="h-12 w-12 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center mb-3">
                <CheckCircle2 size={22} />
              </div>
              <SheetTitle className="font-display tracking-tight text-3xl">
                {t("confirm.great")}
              </SheetTitle>
              <SheetDescription className="text-base text-slate-700 leading-relaxed">
                {t("confirm.line")}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-3 flex items-center gap-2 text-sm text-slate-700">
              <span>{t("confirm.cart_handed_back")}</span>
              <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                <ErpLogo erp={confirmation.erp} size={18} />
                {confirmation.erp.name}
              </span>
            </div>

            <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("confirm.order_id")}</div>
                  <div className="font-mono text-sm font-semibold text-slate-900 mt-0.5">{confirmation.order_id}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("confirm.cart_id")}</div>
                  <div className="font-mono text-sm font-semibold text-slate-900 mt-0.5">{confirmation.cart_id}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("confirm.destination")}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-0.5">{confirmation.erp.name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("confirm.protocol")}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-0.5">{confirmation.erp.protocol}</div>
                </div>
                {confirmation.entity && (
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("entity.legal_name")}</div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">{confirmation.entity.legal_name}</div>
                    <div className="text-[11px] font-mono text-slate-500 mt-0.5">{confirmation.entity.tax_id}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-white border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">{t("confirm.what_happens_next")}</div>
              <ol className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                  Requisition created in {confirmation.erp.name} from the UniBuy™ cart payload
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#0072CE] shrink-0" />
                  Approval workflow runs inside {confirmation.erp.name} per your governance policy
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                  PO issued back to Infosys OMNISupply — order status will refresh in UniBuy™ automatically
                </li>
              </ol>
            </div>

            <Button
              onClick={() => close(false)}
              data-testid="cart-confirm-close"
              className="mt-4 w-full bg-[#0072CE] hover:bg-[#005BA6] text-white rounded-md py-5 font-semibold"
            >
              {t("confirm.continue_shopping")}
            </Button>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
